import React from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  width: auto;
  max-width: 450px;
  min-width: 350px;
  position: relative;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: #6B7280;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.25rem;
  
  &:hover {
    background-color: #F3F4F6;
    color: #374151;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
  margin-right: 2rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #ECECEC;
  margin: 0.5rem 0;
  width: 100%;
`;

const Section = styled.div`
  margin-bottom: 0.75rem;
`;

const SectionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const SectionContent = styled.div`
  font-size: 0.875rem;
  color: #6B7280;
  line-height: 1.4;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  color: #6F6F6F;
  font-size: 16px;
  font-weight: 400;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const ParameterSection = styled.div`
  margin-bottom: 0.5rem;
`;

const ParameterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const ParameterHeader = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  padding: 0.5rem;
  text-align: center;
`;

const ParameterRow = styled.div`
  display: contents;
`;

const ParameterCell = styled.div`
  font-size: 0.875rem;
  color: #6B7280;
  padding: 0.25rem;
  text-align: center;
`;

const SaveButton = styled.button`
  background-color: #00B5B8;
  color: #FFFFFF;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  width: fit-content;
  margin-top: 0.5rem;
  
  &:hover {
    background-color: #0891B2;
  }
`;

const TemplateDetailModal = ({ isOpen, onClose, templateData }) => {
  if (!isOpen || !templateData) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>
        
        <ModalTitle>Template</ModalTitle>
        <Divider />
        
        <Section>
          <SectionTitle>ชื่อ Template</SectionTitle>
          <SectionContent>{templateData.templateName}</SectionContent>
        </Section>

        <Section>
          <SectionTitle>ชื่อผู้สร้าง</SectionTitle>
          <SectionContent>{templateData.createdBy}</SectionContent>
        </Section>

        <Section>
          <SectionTitle>ป้ายกำกับ (Tag)</SectionTitle>
          <TagsContainer>
            {templateData.tags?.map((tag, index) => (
              <Tag key={index}>{tag}</Tag>
            ))}
          </TagsContainer>
        </Section>

        <TwoColumnGrid>
          <Section>
            <SectionTitle>สำหรับเพศ</SectionTitle>
            <SectionContent>{templateData.gender}</SectionContent>
          </Section>

          <Section>
            <SectionTitle>ช่วงอายุ</SectionTitle>
            <SectionContent>{templateData.ageRange} ปี</SectionContent>
          </Section>
        </TwoColumnGrid>

        <Divider />

        <ParameterSection>
          <ParameterGrid>
            <ParameterHeader>พารามิเตอร์</ParameterHeader>
            <ParameterHeader>ค่าปกติ</ParameterHeader>
            <ParameterHeader>ค่าที่กำหนด</ParameterHeader>
            
            {templateData.parameters?.map((param, index) => (
              <ParameterRow key={index}>
                <ParameterCell>{param}</ParameterCell>
                <ParameterCell>{templateData.normalValues?.[index] ?? '-'}</ParameterCell>
                <ParameterCell>{templateData.setValues?.[index] ?? '-'}</ParameterCell>
              </ParameterRow>
            ))}
          </ParameterGrid>
        </ParameterSection>

        <Divider />

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <SaveButton onClick={onClose}>
            ปิดหน้าต่าง
          </SaveButton>
        </div>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default TemplateDetailModal;